import { EltType } from 'legacy/lut/classes/models/partner360/ELT/EltType'
import { FORM_CODE, FORM_ID, IELTData } from 'legacy/lut/classes/models/partner360/ELT/IELTData'
import { IFormPartnerContacts } from 'legacy/lut/classes/models/partner360/IFormPartnerContacts'
import { downloadAsFile } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/files/downloadAsFile'
import { TImagesFields } from 'legacy/lut/components/widgets/public/partner360/ELT/models/ImageUpload'

import { calculateFileLink, saveFile } from 'core/classes/helpers/saveFiles'
import Logger from 'core/utils/Logger'

import { getPrevEltVersionPictures } from '../ui/versions/helpers/getPrevEltVersionPictures'

import { getPartnerGMManagerWithImage } from './getPartnerGMManagerWithImage'

interface IUploadImagesFromPrevVersion {
  partnerId: string | null
  groupId: number | null
  eltType: EltType
  item: IELTData | null
}

const copyOptionalImage = async (copy: () => Promise<string>): Promise<string> => {
  try {
    return await copy()
  } catch (error) {
    Logger.error('Failed to copy an optional ELT image', error)
    return ''
  }
}

const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0

const getFileNameByField = (data: IELTData, field: TImagesFields) =>
  field === 'Picture' ? data.Picture : data.Logo

const getGMManagerImageFileName = (data: IFormPartnerContacts) => data.Image

const calculatePrevDocUrl = (prev: IELTData, field: TImagesFields) =>
  calculateFileLink({
    formId: FORM_ID,
    entryId: prev.EntryId,
    UID: prev.UID,
    fileName: getFileNameByField(prev, field)!,
    fieldName: field,
  })

const calculateCurrentDocUrl = (current: IELTData, prev: IELTData, field: TImagesFields) =>
  calculateFileLink({
    formId: FORM_ID,
    entryId: current.EntryId,
    UID: current.UID,
    fileName: getFileNameByField(prev, field)!,
    fieldName: field,
  })

const calculateGMManagerImageUrl = (gm: IFormPartnerContacts) =>
  calculateFileLink({
    formId: 700,
    entryId: gm.EntryId,
    UID: gm.UID,
    fileName: getGMManagerImageFileName(gm)!,
    fieldName: 'Image',
  })

const saveFieldFile = async (field: TImagesFields, file: File, current: IELTData) =>
  saveFile({
    formId: FORM_ID,
    formCode: FORM_CODE,
    fieldName: field,
    models: [{ file, EntryId: current.EntryId, UID: current.UID }],
  })

const copyFieldFromPrevVersion = async ({
  prevVersion,
  currentItem,
  field,
}: {
  prevVersion: IELTData
  currentItem: IELTData
  field: TImagesFields
}): Promise<string> => {
  const fileName = getFileNameByField(prevVersion, field)

  if (!isNonEmptyString(fileName)) {
    return ''
  }

  const prevUrl = calculatePrevDocUrl(prevVersion, field)
  const file = await downloadAsFile(prevUrl, fileName)

  if (file) {
    await saveFieldFile(field, file, currentItem)

    return calculateCurrentDocUrl(currentItem, prevVersion, field)
  }
  return ''
}

const copyPictureFromGMManagerIfNeeded = async ({
  partnerId,
  currentItem,
}: {
  partnerId: string | null
  currentItem: IELTData
}): Promise<string> => {
  if (!isNonEmptyString(partnerId)) {
    return ''
  }

  const gm = (await getPartnerGMManagerWithImage(partnerId)) as IFormPartnerContacts | null
  const imageFileName = gm ? getGMManagerImageFileName(gm) : null

  if (!gm || !isNonEmptyString(imageFileName)) {
    return ''
  }

  const gmUrl = calculateGMManagerImageUrl(gm)
  const file = await downloadAsFile(gmUrl, imageFileName)

  if (file) {
    await saveFieldFile('Picture', file, currentItem)

    return calculateFileLink({
      formId: FORM_ID,
      entryId: currentItem.EntryId,
      UID: currentItem.UID,
      fileName: imageFileName,
      fieldName: 'Picture',
    })
  }
  return ''
}

export const uploadImagesFromPrevVersion = async ({
  partnerId,
  eltType,
  item,
  groupId,
}: IUploadImagesFromPrevVersion) => {
  if (!item) {
    return { pictureUrlCurrentDoc: '', logoUrlCurrentDoc: '' }
  }

  const prevVersion = await getPrevEltVersionPictures({
    partnerId,
    eltType,
    groupId,
    currentEntryId: item.EntryId,
  })

  if (!prevVersion) {
    return { pictureUrlCurrentDoc: '', logoUrlCurrentDoc: '' }
  }

  const [pictureFromPrev, logoUrlCurrentDoc] = await Promise.all([
    copyOptionalImage(() =>
      copyFieldFromPrevVersion({ prevVersion, currentItem: item, field: 'Picture' })
    ),
    copyOptionalImage(() =>
      copyFieldFromPrevVersion({ prevVersion, currentItem: item, field: 'Logo' })
    ),
  ])

  const pictureUrlCurrentDoc =
    pictureFromPrev !== ''
      ? pictureFromPrev
      : await copyOptionalImage(() =>
          copyPictureFromGMManagerIfNeeded({ partnerId, currentItem: item })
        )

  return { pictureUrlCurrentDoc, logoUrlCurrentDoc }
}
